import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

const MatrixContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
    overflow-x: hidden;
    overflow-y: auto;
    max-height: 66.6vh;
    padding: 0.25rem 0.25rem 0.5rem 0.25rem;
    width: 100%;
`;

const MatrixItem = styled.button`
    border: none;
    border-radius: 3px;
    background-color: ${(props) => (!props.imported ? `#ffffff` : `#fafafa`)};
    :hover {
        background-color: ${(props) => (!props.imported ? `#f0f8fe` : `#fafafa`)};
        cursor: ${(props) => (!props.imported ? `pointer` : `default`)};
    }
    box-shadow: 0 0 1px rgba(67, 90, 111, 0.3), 0 2px 4px -2px rgba(67, 90, 111, 0.47);
    color: #4a4e51;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
    outline: none;
    margin: 0.42rem 0.5rem 0.15rem 0.5rem;
    padding: 1.133rem 3.5rem 1.133rem 2.25rem;
    position: relative;
    text-align: left;
    transition: background-color 0.2s ease;
    svg {
        display: none;
        position: absolute;
        top: 1rem;
        right: 1rem;
        visibility: hidden;
    }
    ${(props) =>
        props['aria-pressed']
            ? `background-color: #F6FBFE !important;
        svg {
            display: block;
            visibility: visible;
        }`
            : null}
    &:before {
        background-color: ${(props) => (!props.imported ? `#0088cc` : `#36454f`)};
        border-top-left-radius: 10px;
        border-bottom-left-radius: 10px;
        content: ' ';
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        width: 3px;
    }
`;

const MatrixKVRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    /*margin: 0.066rem 0;*/
    border-bottom: 1px solid rgba(0, 0, 0, 0.07);
    padding: 0.286rem 0;
    :first-of-type {
        padding-top: 0;
    }
    :last-of-type {
        border-bottom: none;
        padding-bottom: 0;
    }
`;

const MatrixKey = styled.span`
    font-size: 0.8rem;
    font-weight: 700;
    min-width: 15%;
    text-transform: capitalize;
`;

const MatrixVal = styled.span`
    display: block;
    margin: 0 0 0 0.66rem;
    word-break: break-all;
`;

const ImportedBadge = styled.span`
    display: block;
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    background-color: #36454f;
    border-radius: 4px;
    font-weight: 700;
    font-size: 0.7rem;
    color: #ffffff;
    padding: 0.2rem 0.5rem;
    text-transform: uppercase;
`;

const EmptyDatum = ({ ...props }) => (
    <div className="panel panel-warning">
        <div className="panel-heading">
            <h3 className="panel-title">
                <strong>No {props.itemNomenclaturePlural} Available</strong>
            </h3>
        </div>
        <div className="panel-body">Please check your previous selection, and try again.</div>
    </div>
);

EmptyDatum.propTypes = {
    itemNomenclaturePlural: PropTypes.string
};

const MultiSelectMatrix = ({
    children,
    itemNomenclature,
    itemNomenclaturePlural,
    datum,
    selections,
    onChange,
    ...props
}) => {
    const availableDatum = datum.available;
    const importedDatum = datum.imported;
    return (
        <React.Fragment>
            {children}
            <MatrixContainer>
                {Object.keys(availableDatum).length > 0 ? (
                    <React.Fragment>
                        {Object.keys(availableDatum).map((key, i) => (
                            <MatrixItem
                                key={i}
                                aria-pressed={selections.includes(key)}
                                type="button"
                                onClick={() => onChange(key)}
                                value={key}
                            >
                                {Object.keys(availableDatum[key]).map((datumKey, n) => {
                                    if (
                                        (typeof availableDatum[key][datumKey] === 'string' &&
                                            availableDatum[key][datumKey].length > 0) ||
                                        React.isValidElement(availableDatum[key][datumKey])
                                    ) {
                                        return (
                                            <MatrixKVRow key={n}>
                                                <MatrixKey>{datumKey}:</MatrixKey>
                                                <MatrixVal>{availableDatum[key][datumKey]}</MatrixVal>
                                            </MatrixKVRow>
                                        );
                                    } else {
                                        return null;
                                    }
                                })}
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24">
                                    <path fill="none" d="M0 0h24v24H0V0z" />
                                    <path
                                        fill="#08c"
                                        d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
                                    />
                                </svg>
                            </MatrixItem>
                        ))}
                        {Object.keys(importedDatum).map((key, i) => (
                            <MatrixItem
                                key={i}
                                type="button"
                                aria-pressed={selections.includes(key)}
                                onClick={() => null}
                                value={key}
                                imported={true}
                            >
                                {Object.keys(importedDatum[key]).map((datumKey, n) => {
                                    if (
                                        (typeof importedDatum[key][datumKey] === 'string' &&
                                            importedDatum[key][datumKey].length > 0) ||
                                        React.isValidElement(importedDatum[key][datumKey])
                                    ) {
                                        return (
                                            <MatrixKVRow key={n}>
                                                <MatrixKey>{datumKey}:</MatrixKey>
                                                <MatrixVal>{importedDatum[key][datumKey]}</MatrixVal>
                                            </MatrixKVRow>
                                        );
                                    } else {
                                        return null;
                                    }
                                })}
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24">
                                    <path fill="none" d="M0 0h24v24H0V0z" />
                                    <path
                                        fill="#08c"
                                        d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
                                    />
                                </svg>
                                <ImportedBadge>Imported</ImportedBadge>
                            </MatrixItem>
                        ))}
                    </React.Fragment>
                ) : (
                    <React.Fragment>
                        {Object.keys(importedDatum).length > 0 ? (
                            Object.keys(importedDatum).map((key, i) => (
                                <MatrixItem
                                    key={i}
                                    type="button"
                                    aria-pressed={selections.includes(key)}
                                    onClick={() => null}
                                    value={key}
                                    imported={true}
                                >
                                    {Object.keys(importedDatum[key]).map((datumKey, n) => {
                                        if (
                                            typeof importedDatum[key][datumKey] === 'string' &&
                                            importedDatum[key][datumKey].length > 0
                                        ) {
                                            return (
                                                <MatrixKVRow key={n}>
                                                    <MatrixKey>{datumKey}:</MatrixKey>
                                                    <MatrixVal>{importedDatum[key][datumKey]}</MatrixVal>
                                                </MatrixKVRow>
                                            );
                                        } else {
                                            return null;
                                        }
                                    })}
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24">
                                        <path fill="none" d="M0 0h24v24H0V0z" />
                                        <path
                                            fill="#08c"
                                            d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
                                        />
                                    </svg>
                                    <ImportedBadge>Imported</ImportedBadge>
                                </MatrixItem>
                            ))
                        ) : (
                            <EmptyDatum itemNomenclaturePlural={itemNomenclaturePlural} />
                        )}
                    </React.Fragment>
                )}
            </MatrixContainer>
        </React.Fragment>
    );
};

MultiSelectMatrix.propTypes = {
    children: PropTypes.node,
    datum: PropTypes.object,
    itemNomenclature: PropTypes.string,
    itemNomenclaturePlural: PropTypes.string,
    onChange: PropTypes.func,
    selections: PropTypes.array,
    setRenderStep: PropTypes.func
};

MultiSelectMatrix.defaultProps = {
    datum: {},
    selections: [],
    itemNomenclature: 'Entity',
    itemNomenclaturePlural: 'Entities',
    onChange: () => null
};

export default MultiSelectMatrix;
